import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { RoomObjectAclSchema } from '../../../definitions/schemas';
import { useI18n } from '../../../lib/hook/useI18n';
import { getDisplayName } from '../../../lib/DisplayName';
import RoomObjectAclsInterface from '../../../graphql/Rooms/RoomObjectAclsInterface';
import useResource from '../../../lib/hook/useResource';
import { RoomContentContextProps } from '../../../lib/RoomContentContext';
import { RoomObjectAclType } from '../../../enums/RoomObjectAcl';
import { getAllPages } from '../../../lib/helper/ListingHelper';
import { ascendingSort } from '../../../lib/Array';
import { ROOM_OBJECT_ACLS_LIMIT } from '../../../enums/Constraints';

const MAX_GUESTS = 5;

type PositionStyle = {
  left?: number;
  top?: number;
  zIndex?: number;
};

export interface ContentAccessListProps {
  className?: string;
  contentId?: string;
  contextProps?: RoomContentContextProps;
  disablePortal?: boolean;
  loadFromResource?: boolean;
  objectAcls?: RoomObjectAclSchema[];
  onlyIcon?: boolean;
  positionStyle?: PositionStyle;
  roomId?: string;
  tileMode?: boolean;
}

// this component handles 2 scenarios:
// 1. when the objectAcls are provided as a prop comes from step3(review) or room preview of the content creation/edit flow
// 2. when the objectAcls are fetched from the server in the case of room view

const ContentAccessList: React.FC<ContentAccessListProps> = (
  props: ContentAccessListProps
): JSX.Element => {
  const { t } = useI18n();

  const {
    className,
    contentId,
    contextProps,
    disablePortal = false,
    loadFromResource,
    objectAcls: initialObjectAcls = [],
    onlyIcon = false,
    positionStyle,
    roomId,
    tileMode,
  } = props;

  const [open, setOpen] = useState(false);

  const [objectAcls, objectAclsReady, , setObjectAcls] = useResource(
    () => loadObjectAccessAcls(),
    initialObjectAcls,
    [],
    loadFromResource && open
  );

  useEffect(() => {
    setObjectAcls(initialObjectAcls);
  }, [initialObjectAcls, setObjectAcls]);

  const loadObjectAccessAcls = async () => {
    if (!(contentId && roomId)) {
      return initialObjectAcls;
    }

    const { visitorId, isContact } = contextProps!;

    const response = await getAllPages(
      (input) =>
        new RoomObjectAclsInterface(
          isContact ? visitorId : undefined
        ).getRoomObjectAcls({
          ...input,
          objectDataId: contentId,
          objectDataType: RoomObjectAclType.Content,
          roomId,
        }),
      ROOM_OBJECT_ACLS_LIMIT
    );

    return response.nodes;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = async () => {
    setOpen(true);
  };

  const renderGuests = (): JSX.Element[] => {
    const nodes = objectAcls
      .slice(0, MAX_GUESTS)
      .sort((a, b) =>
        ascendingSort(getDisplayName(a.guest!), getDisplayName(b.guest!))
      );

    return nodes.map((object) => (
      <Typography
        component="li"
        key={`${object.id}-${object.guestType}-${object.guestId}`}
        sx={{ my: 0.5 }}
        variant="inherit"
      >
        {getDisplayName(object.guest!, {
          fullEmail: true,
        })}
      </Typography>
    ));
  };

  const renderMoreDetails = (): JSX.Element => {
    if (objectAcls.length <= MAX_GUESTS) {
      return <></>;
    }

    const label = t('room.overview.guest_list.more', {
      amount: objectAcls.length - MAX_GUESTS,
    });

    return (
      <Typography component="li" variant="inherit" sx={{ my: 0.5 }}>
        {label}
      </Typography>
    );
  };

  const renderTooltipTitle = (): JSX.Element => {
    if (loadFromResource && !objectAclsReady) {
      return (
        <Box color="white">
          <CircularProgress color="inherit" size={18} />
        </Box>
      );
    }

    return (
      <>
        <Typography component="div" fontWeight="bold" variant="inherit">
          {t('content_permission.tooltip.visible_to')} <br />
        </Typography>
        <Box component="ul" sx={{ margin: 0, pl: 2.25, mt: 1 }}>
          {renderGuests()}
          {renderMoreDetails()}
        </Box>
      </>
    );
  };

  const renderContent = () => {
    if (
      (!loadFromResource && !initialObjectAcls.length) ||
      (loadFromResource && objectAclsReady && !objectAcls.length)
    ) {
      return <></>;
    }

    const leftStyle = tileMode ? 1.5 : 7.25;
    const topStyle = tileMode ? 1.5 : 0.75;

    const iconWrapper = (
      <Box
        className={className}
        position="absolute"
        sx={(theme) => ({
          background: 'var(--background-color-dark-transparent)',
          borderRadius: theme.spacing(0.5),
          boxShadow: '0px 0px 0px 1px var(--gray-color-shadow) inset',
          color: theme.palette.common.white,
          height: theme.spacing(3.75),
          left: theme.spacing(positionStyle?.left || leftStyle),
          padding: theme.spacing(0.5),
          top: theme.spacing(positionStyle?.top || topStyle),
          zIndex: positionStyle?.zIndex || (onlyIcon ? 1 : 'inherit'),
        })}
      >
        <LockIcon fontSize="small" />
      </Box>
    );

    if (onlyIcon) {
      return iconWrapper;
    }

    return (
      <Tooltip
        arrow
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
        placement="top"
        PopperProps={{ disablePortal }}
        title={renderTooltipTitle()}
        enterTouchDelay={0}
        leaveTouchDelay={5000}
      >
        {iconWrapper}
      </Tooltip>
    );
  };

  return renderContent();
};

export default ContentAccessList;
